/* eslint-disable @typescript-eslint/no-explicit-any */
import styled, { x } from '@xstyled/styled-components';
import React, { Children, Component } from 'react';
import { t } from 'ttag';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import {
  AUDIO,
  HORIZONTAL_AUDIO_PLAYER,
  EMAIL,
  IMAGE,
  VIDEO
} from '../itemIds';
import { isUserNameLine, UserNameLine } from './UserNameLine';

interface Props {
  from: string;
  subject: string;
}

const Header = styled.divBox`
  background-color: greyLight;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  height: 46px;
  padding-top: 13px;
  text-align: center;

  > b {
    font-family: Raleway;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 150%;

    padding-left: 80px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    padding-right: 20px;
    text-align: left;
  }
`;

/* Fills the remaining height of the email so the player block is pushed down. */
const Content = styled.divBox`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  padding-bottom: 0px;
`;

const Body = styled.divBox`
  flex: 1;
  font-family: Raleway;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 150%;
  color: black;
  font-weight: 100;
  & strong {
    font-weight: 700;
    font-size: 17px;
  }
`;

/* The audio player block, always pinned to the bottom of the email. */
const PlayerBlock = styled.divBox`
  margin-top: auto;
  padding-top: 20px;

  .horizontalAudioCardPlayer {
    padding: 0 !important;
  }
`;

const DotsBlock = styled.div`
  width: 60px;
  height: 30px;
  position: absolute;
  margin-left: 20px;
  padding-top: 5px;
  > div:nth-child(1) {
    background-color: #f7326d;
  }
  > div:nth-child(2) {
    background-color: #fdd15f;
  }
  > div:nth-child(3) {
    background-color: #00a88a;
  }
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  float: left;
  margin-right: 4px;
`;

const Dots = () => {
  return (
    <DotsBlock>
      <Dot />
      <Dot />
      <Dot />
    </DotsBlock>
  );
};

const Attachments = styled.divBox`
  margin-top: 20px;
  padding-top: 20px;
  > b {
    font-size: 12px;
  }
  > div {
    float: left;
    width: 100%;
    height: 71px;
    margin-right: 18px;
    margin-top: 11px;
  }
  & .fileName {
    font-family: Raleway;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
  }
`;

export type EmailDefinition = ItemDefinition<typeof EMAIL, Props>;

export class Email extends Item<typeof EMAIL, Props> {
  protected render({ id, children }: RenderProps<Props>) {
    const childArray = Children.toArray(children);

    const getFileName = (c: any, i: number) => {
      let fileExtension = '.jpeg';

      if (c?.props?.type == VIDEO) {
        fileExtension = '.mp4';
      }

      if (c?.props?.type == AUDIO) {
        fileExtension = '.mp3';
      }

      if (c?.props?.type == HORIZONTAL_AUDIO_PLAYER) {
        return null;
      }

      return (
        <span className="fileName">{`${c?.props?.type}${i}${fileExtension}`}</span>
      );
    };

    const isAudioPlayer = (c: any) => c.props.type == HORIZONTAL_AUDIO_PLAYER;

    const isFileAttachment = (c: any) =>
      c.props.type == IMAGE || c.props.type == AUDIO || c.props.type == VIDEO;

    const fileAttachmentsExist = childArray.some(isFileAttachment);
    const audioPlayers = childArray.filter(isAudioPlayer);

    return (
      <Flex variant="center">
        <Card
          backgroundColor="white"
          w={{ _: '330px', sm: '330px', lg: '400px' }}
          paddingBottom="20px"
          minH="300px"
          display="flex"
          flexDirection="column"
          position="relative"
          color="greyDark"
          overflow="hidden"
          className="emailStyleForDarkTheme"
        >
          <Header>
            <Dots />
            <x.b>{t`You've got mail!`}</x.b>
          </Header>
          <Content>
            <Body>
              {childArray.map((c: Component<RenderProps<void>>, i) => {
                if (isFileAttachment(c) || isAudioPlayer(c)) {
                  return false;
                }
                if (isUserNameLine(c, id)) {
                  return <UserNameLine key={i}>{c}</UserNameLine>;
                }
                return <x.div key={i}>{c}</x.div>;
              })}
              {fileAttachmentsExist && (
                <Attachments>
                  <x.b style={{ display: 'block', clear: 'both' }}>
                    {t`Attachments`}:
                  </x.b>
                  {childArray.map((c: any, i) => {
                    if (!isFileAttachment(c)) {
                      return false;
                    }
                    return (
                      <x.div key={i}>
                        {c}
                        {getFileName(c, i)}
                      </x.div>
                    );
                  })}
                </Attachments>
              )}
            </Body>
            {audioPlayers.length > 0 && (
              <PlayerBlock>
                <x.b
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 'bolder',
                    fontFamily: 'Raleway',
                    fontStyle: 'normal',
                    color: 'var(--color-black)'
                  }}
                >
                  {t`Attachments`}:
                </x.b>
                {audioPlayers.map((c: any, i) => (
                  <x.div key={i}>{c}</x.div>
                ))}
              </PlayerBlock>
            )}
          </Content>
        </Card>
      </Flex>
    );
  }
}

registry[EMAIL] = Email;
